import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Download,
  LockKeyhole,
  RefreshCw,
  Trash2,
  UsersRound
} from "lucide-react";

import type {
  ChildProfile,
  ParentReport,
  ProgressRepository
} from "@/domain";

import type { ParentAccessStore } from "./access";
import {
  ParentConfirmationDialog,
  type ParentConfirmation
} from "./ParentConfirmationDialog";
import { ParentPinGate } from "./ParentPinGate";
import { ParentReportView } from "./ParentReportView";
import {
  downloadProfileExport,
  type ProfileExportDownloader
} from "./export";
import {
  createParentPinHash,
  isValidParentPin,
  verifyParentPin
} from "./pin";
import { loadParentReport } from "./report";
import "./ParentCenter.css";

type AccessMode = "loading" | "setup" | "locked" | "unlocked" | "error";
type ReportMode = "idle" | "loading" | "ready" | "empty" | "error";
type PendingAction = "pin" | "export" | "delete" | "clear";

interface Feedback {
  kind: "success" | "error";
  message: string;
}

interface AccessState {
  repository: ProgressRepository;
  parentAccessStore: ParentAccessStore;
  profileId: string;
  reloadKey: number;
  mode: AccessMode;
  parentPinHash?: string;
}

export type ParentClearAllOutcome =
  | { status: "complete" }
  | { status: "parent-access-retained"; warning: string };

export interface ParentCenterProps {
  repository: ProgressRepository;
  parentAccessStore: ParentAccessStore;
  profileId: string;
  downloadExport?: ProfileExportDownloader;
  onProfileDeleted: (profileId: string) => void;
  onAllProfilesCleared: (outcome: ParentClearAllOutcome) => void;
}

const PARENT_ACCESS_CLEAR_WARNING =
  "小船员资料已清空，但家长密码暂时无法清除，请稍后再试。";

export function ParentCenter({
  repository,
  parentAccessStore,
  profileId,
  downloadExport = downloadProfileExport,
  onProfileDeleted,
  onAllProfilesCleared
}: ParentCenterProps) {
  const accessRequestRef = useRef(0);
  const reportRequestRef = useRef(0);
  const activeExportRequestRef = useRef<number | undefined>(undefined);
  const [accessReloadKey, setAccessReloadKey] = useState(0);
  const [accessState, setAccessState] = useState<AccessState>(() => ({
    repository,
    parentAccessStore,
    profileId,
    reloadKey: 0,
    mode: "loading"
  }));
  const isCurrentAccessState =
    accessState.repository === repository &&
    accessState.parentAccessStore === parentAccessStore &&
    accessState.profileId === profileId &&
    accessState.reloadKey === accessReloadKey;
  const accessMode = isCurrentAccessState ? accessState.mode : "loading";
  const parentPinHash = isCurrentAccessState
    ? accessState.parentPinHash
    : undefined;
  const [pin, setPin] = useState("");
  const [confirmationPin, setConfirmationPin] = useState("");
  const [pinError, setPinError] = useState<string>();
  const [profiles, setProfiles] = useState<ChildProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [report, setReport] = useState<ParentReport>();
  const [reportMode, setReportMode] = useState<ReportMode>("idle");
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [confirmation, setConfirmation] = useState<ParentConfirmation>();
  const [feedback, setFeedback] = useState<Feedback>();

  useEffect(() => {
    const requestId = accessRequestRef.current + 1;
    accessRequestRef.current = requestId;
    reportRequestRef.current += 1;

    void Promise.all([
      repository.getProfile(profileId),
      parentAccessStore.load()
    ]).then(
      ([profile, loadedParentPinHash]) => {
        if (requestId !== accessRequestRef.current) {
          return;
        }

        setPin("");
        setConfirmationPin("");
        setPinError(undefined);
        setProfiles([]);
        setSelectedProfileId("");
        setReport(undefined);
        setReportMode("idle");
        setPendingAction(undefined);
        setConfirmation(undefined);
        setFeedback(undefined);

        if (profile === undefined) {
          setAccessState({
            repository,
            parentAccessStore,
            profileId,
            reloadKey: accessReloadKey,
            mode: "error"
          });
          return;
        }

        setAccessState({
          repository,
          parentAccessStore,
          profileId,
          reloadKey: accessReloadKey,
          mode: loadedParentPinHash === undefined ? "setup" : "locked",
          parentPinHash: loadedParentPinHash
        });
      },
      () => {
        if (requestId === accessRequestRef.current) {
          setPin("");
          setConfirmationPin("");
          setPinError(undefined);
          setProfiles([]);
          setSelectedProfileId("");
          setReport(undefined);
          setReportMode("idle");
          setPendingAction(undefined);
          setConfirmation(undefined);
          setFeedback(undefined);
          setAccessState({
            repository,
            parentAccessStore,
            profileId,
            reloadKey: accessReloadKey,
            mode: "error"
          });
        }
      }
    );

    return () => {
      accessRequestRef.current += 1;
      reportRequestRef.current += 1;
    };
  }, [accessReloadKey, parentAccessStore, profileId, repository]);

  useEffect(
    () =>
      parentAccessStore.subscribe(() => {
        accessRequestRef.current += 1;
        reportRequestRef.current += 1;
        setAccessReloadKey((value) => value + 1);
      }),
    [parentAccessStore]
  );

  async function refreshReports(preferredProfileId?: string): Promise<void> {
    const requestId = reportRequestRef.current + 1;
    const accessRequestId = accessRequestRef.current;
    reportRequestRef.current = requestId;
    setReportMode("loading");
    setReport(undefined);

    try {
      const nextProfiles = await repository.listProfiles();
      if (
        requestId !== reportRequestRef.current ||
        accessRequestId !== accessRequestRef.current
      ) {
        return;
      }

      if (nextProfiles.length === 0) {
        setProfiles([]);
        setSelectedProfileId("");
        setReportMode("empty");
        return;
      }

      const firstProfile = nextProfiles[0];
      if (firstProfile === undefined) {
        setProfiles([]);
        setSelectedProfileId("");
        setReportMode("empty");
        return;
      }

      const nextProfileId = nextProfiles.some(
        (profile) => profile.id === preferredProfileId
      )
        ? (preferredProfileId ?? firstProfile.id)
        : firstProfile.id;

      setProfiles(nextProfiles);
      setSelectedProfileId(nextProfileId);

      const nextReport = await loadParentReport(repository, nextProfileId);
      if (
        requestId !== reportRequestRef.current ||
        accessRequestId !== accessRequestRef.current
      ) {
        return;
      }

      setReport(nextReport);
      setReportMode("ready");
    } catch {
      if (
        requestId === reportRequestRef.current &&
        accessRequestId === accessRequestRef.current
      ) {
        setReport(undefined);
        setReportMode("error");
      }
    }
  }

  function updatePin(value: string): void {
    setPin(normalizePinInput(value));
    setPinError(undefined);
  }

  function updateConfirmationPin(value: string): void {
    setConfirmationPin(normalizePinInput(value));
    setPinError(undefined);
  }

  async function handlePinSubmit(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    if (!isValidParentPin(pin)) {
      setPinError("请输入 4 位数字密码。");
      return;
    }

    if (accessMode === "setup") {
      if (pin !== confirmationPin) {
        setConfirmationPin("");
        setPinError("两次输入不一致，请重新输入。");
        return;
      }

      await setupParentPin();
      return;
    }

    if (accessMode === "locked") {
      if (parentPinHash === undefined) {
        setAccessState((currentState) => ({
          ...currentState,
          mode: "setup"
        }));
        return;
      }

      await unlockParentCenter(parentPinHash);
    }
  }

  async function setupParentPin(): Promise<void> {
    const requestId = accessRequestRef.current;
    setPendingAction("pin");
    setPinError(undefined);

    try {
      const createdParentPinHash = await createParentPinHash(pin);

      setPin("");
      setConfirmationPin("");
      await parentAccessStore.save(createdParentPinHash);

      if (requestId !== accessRequestRef.current) {
        return;
      }

      setAccessState((currentState) => ({
        ...currentState,
        mode: "unlocked",
        parentPinHash: createdParentPinHash
      }));
      await refreshReports(profileId);
    } catch {
      if (requestId === accessRequestRef.current) {
        setPin("");
        setConfirmationPin("");
        setPinError("暂时无法保存家长密码，请稍后再试。");
      }
    } finally {
      if (requestId === accessRequestRef.current) {
        setPendingAction(undefined);
      }
    }
  }

  async function unlockParentCenter(
    storedParentPinHash: string
  ): Promise<void> {
    const requestId = accessRequestRef.current;
    setPendingAction("pin");
    setPinError(undefined);
    const enteredPin = pin;
    setPin("");

    try {
      const isVerified = await verifyParentPin(
        enteredPin,
        storedParentPinHash
      );
      if (requestId !== accessRequestRef.current) {
        return;
      }

      if (!isVerified) {
        setPinError("密码不正确，请再试一次。");
        return;
      }

      setAccessState((currentState) => ({
        ...currentState,
        mode: "unlocked"
      }));
      await refreshReports(profileId);
    } catch {
      if (requestId === accessRequestRef.current) {
        setPinError("暂时无法验证家长密码，请稍后再试。");
      }
    } finally {
      if (requestId === accessRequestRef.current) {
        setPendingAction(undefined);
      }
    }
  }

  function resetAuthorization(
    mode: "setup" | "locked",
    nextParentPinHash: string | undefined = parentPinHash
  ): void {
    reportRequestRef.current += 1;
    setAccessState((currentState) => ({
      ...currentState,
      mode,
      parentPinHash: nextParentPinHash
    }));
    setPin("");
    setConfirmationPin("");
    setPinError(undefined);
    setProfiles([]);
    setSelectedProfileId("");
    setReport(undefined);
    setReportMode("idle");
    setPendingAction(undefined);
    setConfirmation(undefined);
    setFeedback(undefined);
  }

  function lockParentCenter(): void {
    resetAuthorization(
      parentPinHash === undefined ? "setup" : "locked",
      parentPinHash
    );
  }

  function requestProfileExport(): void {
    const selectedProfile = profiles.find(
      (profile) => profile.id === selectedProfileId
    );
    if (selectedProfile === undefined) {
      return;
    }

    setFeedback(undefined);
    setConfirmation({
      kind: "export",
      profileId: selectedProfile.id,
      profileName: selectedProfile.nickname
    });
  }

  async function completeProfileExport(
    exportConfirmation: Extract<ParentConfirmation, { kind: "export" }>
  ): Promise<void> {
    const requestId = accessRequestRef.current;
    if (activeExportRequestRef.current === requestId) {
      return;
    }

    activeExportRequestRef.current = requestId;
    setPendingAction("export");
    setFeedback(undefined);

    try {
      const serializedProfile = await repository.exportProfile(
        exportConfirmation.profileId
      );
      if (requestId !== accessRequestRef.current) {
        return;
      }

      downloadExport(serializedProfile);
      setConfirmation(undefined);
      setFeedback({ kind: "success", message: "资料已导出为 JSON 文件。" });
    } catch {
      if (requestId === accessRequestRef.current) {
        setConfirmation(undefined);
        setFeedback({
          kind: "error",
          message: "暂时无法导出资料，请稍后再试。"
        });
      }
    } finally {
      if (activeExportRequestRef.current === requestId) {
        activeExportRequestRef.current = undefined;
      }
      if (requestId === accessRequestRef.current) {
        setPendingAction(undefined);
      }
    }
  }

  function requestProfileDeletion(): void {
    if (selectedProfileId !== "") {
      setFeedback(undefined);
      setConfirmation({
        kind: "delete",
        profileId: selectedProfileId,
        step: 1
      });
    }
  }

  function requestClearAll(): void {
    if (profiles.length > 0) {
      setFeedback(undefined);
      setConfirmation({ kind: "clear", step: 1 });
    }
  }

  function handleConfirmation(): void {
    if (confirmation === undefined) {
      return;
    }

    if (confirmation.kind === "export") {
      void completeProfileExport(confirmation);
      return;
    }

    if (confirmation.step === 1) {
      setConfirmation({ ...confirmation, step: 2 });
      return;
    }

    void completeDestructiveAction(confirmation);
  }

  async function completeDestructiveAction(
    finalConfirmation: ParentConfirmation
  ): Promise<void> {
    const requestId = accessRequestRef.current;
    setPendingAction(finalConfirmation.kind);

    if (finalConfirmation.kind === "delete") {
      try {
        await repository.deleteProfile(finalConfirmation.profileId);
      } catch {
        if (requestId === accessRequestRef.current) {
          setConfirmation(undefined);
          setFeedback({
            kind: "error",
            message: "暂时无法删除，资料仍按原样显示。"
          });
          setPendingAction(undefined);
        }
        return;
      }

      if (requestId === accessRequestRef.current) {
        setConfirmation(undefined);

        if (finalConfirmation.profileId === profileId) {
          resetAuthorization(
            parentPinHash === undefined ? "setup" : "locked",
            parentPinHash
          );
        } else {
          setFeedback({ kind: "success", message: "资料已删除。" });
        }
      }

      onProfileDeleted(finalConfirmation.profileId);

      if (
        requestId === accessRequestRef.current &&
        finalConfirmation.profileId !== profileId
      ) {
        await refreshReports();
        setPendingAction(undefined);
      }
      return;
    }

    try {
      await repository.clearAll();
    } catch {
      if (requestId === accessRequestRef.current) {
        setConfirmation(undefined);
        setFeedback({
          kind: "error",
          message: "暂时无法清空，资料仍按原样显示。"
        });
        setPendingAction(undefined);
      }
      return;
    }

    let accessClearFailed = false;
    try {
      await parentAccessStore.clear();
    } catch {
      accessClearFailed = true;
    }

    if (requestId === accessRequestRef.current) {
      resetAuthorization(
        accessClearFailed && parentPinHash !== undefined
          ? "locked"
          : "setup",
        accessClearFailed ? parentPinHash : undefined
      );

      if (accessClearFailed) {
        setPinError(PARENT_ACCESS_CLEAR_WARNING);
      }
    }

    const outcome: ParentClearAllOutcome = accessClearFailed
      ? {
          status: "parent-access-retained",
          warning: PARENT_ACCESS_CLEAR_WARNING
        }
      : { status: "complete" };
    onAllProfilesCleared(outcome);
  }

  if (accessMode === "loading") {
    return (
      <section className="parent-center parent-center--gate">
        <div className="parent-center__gate" role="status">
          <UsersRound aria-hidden="true" className="parent-center__loading-icon" />
          <h1>家长空间</h1>
          <p>正在读取本机资料...</p>
        </div>
      </section>
    );
  }

  if (accessMode === "error") {
    return (
      <section className="parent-center parent-center--gate">
        <div className="parent-center__gate">
          <h1>暂时无法打开家长空间</h1>
          <p role="alert">本机资料读取失败，请稍后重试。</p>
          <button
            className="parent-center__secondary-command"
            onClick={() => setAccessReloadKey((value) => value + 1)}
            type="button"
          >
            <RefreshCw aria-hidden="true" />
            重试
          </button>
        </div>
      </section>
    );
  }

  if (accessMode === "setup" || accessMode === "locked") {
    return (
      <section className="parent-center parent-center--gate">
        <ParentPinGate
          busy={pendingAction === "pin"}
          confirmationPin={confirmationPin}
          error={pinError}
          mode={accessMode}
          onConfirmationPinChange={updateConfirmationPin}
          onPinChange={updatePin}
          onSubmit={(event) => void handlePinSubmit(event)}
          pin={pin}
        />
      </section>
    );
  }

  const isBusy = pendingAction !== undefined;

  return (
    <section
      aria-busy={isBusy}
      aria-labelledby="parent-center-title"
      className="parent-center"
    >
      <header className="parent-center__header">
        <div>
          <p className="parent-center__eyebrow">家长空间</p>
          <h1 id="parent-center-title">学习报告与资料</h1>
        </div>
        <button
          className="parent-center__secondary-command"
          disabled={isBusy}
          onClick={lockParentCenter}
          type="button"
        >
          <LockKeyhole aria-hidden="true" />
          退出家长空间
        </button>
      </header>

      {feedback ? (
        <p
          className={
            "parent-center__message parent-center__message--" + feedback.kind
          }
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      ) : null}

      <section
        aria-label="资料操作"
        className="parent-center__toolbar"
      >
        <label className="parent-center__profile-picker">
          查看小船员
          <select
            disabled={isBusy || profiles.length === 0}
            onChange={(event) => {
              const nextProfileId = event.currentTarget.value;
              setSelectedProfileId(nextProfileId);
              void refreshReports(nextProfileId);
            }}
            value={selectedProfileId}
          >
            {profiles.length === 0 ? (
              <option value="">暂无资料</option>
            ) : (
              profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.nickname}
                </option>
              ))
            )}
          </select>
        </label>
        <div className="parent-center__toolbar-actions">
          <button
            className="parent-center__toolbar-command"
            disabled={isBusy || selectedProfileId === ""}
            onClick={(event) => {
              event.currentTarget.focus();
              requestProfileExport();
            }}
            type="button"
          >
            <Download aria-hidden="true" />
            导出 JSON
          </button>
          <button
            className="parent-center__toolbar-command parent-center__toolbar-command--danger"
            disabled={isBusy || selectedProfileId === ""}
            onClick={requestProfileDeletion}
            type="button"
          >
            <Trash2 aria-hidden="true" />
            删除当前资料
          </button>
        </div>
      </section>

      {reportMode === "loading" ? (
        <p className="parent-center__loading" role="status">
          正在整理学习报告...
        </p>
      ) : null}

      {reportMode === "ready" && report ? (
        <ParentReportView report={report} />
      ) : null}

      {reportMode === "empty" ? (
        <div className="parent-center__empty" role="status">
          <UsersRound aria-hidden="true" />
          <h2>暂无小船员资料</h2>
          <p>设备上的资料已清空。</p>
        </div>
      ) : null}

      {reportMode === "error" ? (
        <div className="parent-center__empty">
          <h2>报告暂时无法显示</h2>
          <p role="alert">本机资料读取失败，请稍后重试。</p>
          <button
            className="parent-center__secondary-command"
            disabled={isBusy}
            onClick={() =>
              void refreshReports(
                selectedProfileId || profileId
              )
            }
            type="button"
          >
            <RefreshCw aria-hidden="true" />
            重试
          </button>
        </div>
      ) : null}

      <section
        aria-labelledby="parent-data-title"
        className="parent-center__data-actions"
      >
        <div>
          <h2 id="parent-data-title">全部资料</h2>
          <p>清空后，设备上的小船员和练习记录将全部移除。</p>
        </div>
        <button
          className="parent-center__toolbar-command parent-center__toolbar-command--danger"
          disabled={isBusy || profiles.length === 0}
          onClick={requestClearAll}
          type="button"
        >
          <Trash2 aria-hidden="true" />
          清空全部资料
        </button>
      </section>

      <ParentConfirmationDialog
        busy={
          pendingAction === "export" ||
          pendingAction === "delete" ||
          pendingAction === "clear"
        }
        confirmation={confirmation}
        onCancel={() => {
          if (!isBusy) {
            setConfirmation(undefined);
          }
        }}
        onConfirm={handleConfirmation}
      />
    </section>
  );
}

function normalizePinInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 4);
}
